import { withSecondsLabel } from '@/shared/lib/utils/string';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';
import { ATTRIBUTE_TYPES, DISPLAY_FORMATS } from '../constants';
import type { AttributeType, DisplayFormat } from '../types';

/** Минимум сведений об атрибуте, по которым определяется вид его значения */
export type AttributeDisplayInfo = {
  /** Название атрибута */
  name?: string | null;
  /** Тип данных атрибута */
  dataType?: AttributeType | string | null;
  /** Формат отображения, заданный у самого атрибута */
  displayFormat?: DisplayFormat | null;
};

/** Формат отображения, которым задаётся вид интервала времени; прочие форматы — `null` */
const asTimeIntervalFormat = (displayFormat?: DisplayFormat | null): TimeIntervalFormat | null =>
  displayFormat === DISPLAY_FORMATS.TIME || displayFormat === DISPLAY_FORMATS.SECOND ? displayFormat : null;

/**
 * Формат интервала времени у числового атрибута: число с таким форматом — тоже интервал
 * времени, только хранимый числом секунд. Без формата число остаётся обычным числом.
 */
const getNumericTimeFormat = (attribute: AttributeDisplayInfo): TimeIntervalFormat | null => {
  const isNumeric = attribute.dataType === ATTRIBUTE_TYPES.INTEGER || attribute.dataType === ATTRIBUTE_TYPES.DECIMAL;
  return isNumeric ? asTimeIntervalFormat(attribute.displayFormat) : null;
};

/**
 * Действующий формат интервала времени для атрибута: формат самого атрибута важнее переданного
 * запасного. Возвращает `null`, если значение атрибута интервалом времени не является.
 */
export const getAttributeTimeFormat = (
  attribute: AttributeDisplayInfo | undefined,
  timeDisplayFormat: TimeIntervalFormat,
): TimeIntervalFormat | null => {
  if (!attribute) return null;

  if (attribute.dataType === ATTRIBUTE_TYPES.TIME) {
    return asTimeIntervalFormat(attribute.displayFormat) ?? timeDisplayFormat;
  }

  return getNumericTimeFormat(attribute);
};

/**
 * Название атрибута для интерфейса: у интервала времени, показываемого числом секунд,
 * к названию добавляется подпись единицы измерения.
 */
export const getAttributeDisplayName = (
  attribute: AttributeDisplayInfo | undefined,
  timeDisplayFormat: TimeIntervalFormat,
  secondsSuffix: string,
): string => withSecondsSuffix(attribute?.name ?? '', attribute, timeDisplayFormat, secondsSuffix);

/**
 * Дополняет уже готовое название подписью единицы измерения, если атрибут показывается
 * числом секунд. Для случаев, когда подпись колонки приходит не из самого атрибута.
 */
export const withSecondsSuffix = (
  title: string,
  attribute: AttributeDisplayInfo | undefined,
  timeDisplayFormat: TimeIntervalFormat,
  secondsSuffix: string,
): string => {
  const timeFormat = getAttributeTimeFormat(attribute, timeDisplayFormat);
  return timeFormat ? withSecondsLabel(title, timeFormat, secondsSuffix) : title;
};
